import * as React from "react";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ErrorIcon from "@mui/icons-material/Error";
import CancelIcon from "@mui/icons-material/Cancel";
import SensorsIcon from "@mui/icons-material/Sensors";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { NavigateFunction } from "react-router-dom";
import { ActionLogsText } from "./SiteAboutPageUtils/actionLogsText";
import { DownloadSiteDetailsText } from "./SiteAboutPageUtils/downloadSiteDetailsText";
import { downloadSiteDetails } from "./SiteAboutPageUtils/downloadSiteDetails";
import { handleConfigureMotion } from "./SiteAboutPageUtils/handleConfigureMotion";
import { getSeeAlertsPath } from "./SiteAboutPageUtils/seeAlertsPath";
import { GenetecAlarmSetupDialog } from "../../../common/GenetecAlarmSetup/GenetecAlarmSetupDialog";
import {
  AlertState,
  Dispatcher,
  SiteInfo,
} from "./SiteAboutPageUtils/siteAboutInterfaces";

export const SiteAboutActionsMenu = ({
  siteInfo,
  permissions,
  configureMotion,
  setBackdropLoadingScreenOn,
  setAlertState,
  setDeleteSiteDialogOpen,
  navigate,
}: {
  siteInfo: SiteInfo;
  permissions: any;
  configureMotion: boolean;
  setBackdropLoadingScreenOn: Dispatcher<boolean>;
  setAlertState: Dispatcher<AlertState>;
  setDeleteSiteDialogOpen: Dispatcher<boolean>;
  navigate: NavigateFunction;
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Same poll-lifetime rules as ConfigureMotionButton: abort on unmount or
  // re-click so Configure Motion never updates state after leaving the page.
  const pollAbort = React.useRef<AbortController | null>(null);
  React.useEffect(() => {
    return () => {
      pollAbort.current?.abort();
    };
  }, []);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isAutopatrol = siteInfo.integration_type_name === "autopatrol";
  const isGenetec = siteInfo.integration_type_name === "genetec";
  // Configure Motion and the Genetec alarm setup change site settings, so
  // they need edit_cameras — arm/disarm alone isn't enough (PROD-56).
  const canEditSite = Boolean(permissions.edit_cameras);
  const [genetecAlarmDialogOpen, setGenetecAlarmDialogOpen] =
    React.useState(false);

  return (
    <div>
      <IconButton
        aria-label="Site about actions"
        aria-controls={open ? "site-about-actions-menu" : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="true"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        size="small"
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="site-about-actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            handleClose();
            downloadSiteDetails(siteInfo.id, siteInfo.name).catch(() => {
              alert(
                "Failed to download the site details report. Please try again."
              );
            });
          }}
          sx={{ fontFamily: "Mulish" }}
        >
          {DownloadSiteDetailsText}
        </MenuItem>
        {canEditSite && configureMotion && (
          <MenuItem
            onClick={() => {
              handleClose();
              pollAbort.current?.abort();
              const controller = new AbortController();
              pollAbort.current = controller;
              setBackdropLoadingScreenOn(true);
              handleConfigureMotion(
                setBackdropLoadingScreenOn,
                setAlertState,
                navigate,
                { signal: controller.signal }
              );
            }}
            sx={{ fontFamily: "Mulish" }}
          >
            <SensorsIcon fontSize="small" />
            &nbsp;Configure Motion
          </MenuItem>
        )}
        {canEditSite && isGenetec && (
          <MenuItem
            onClick={() => {
              handleClose();
              setGenetecAlarmDialogOpen(true);
            }}
            sx={{ fontFamily: "Mulish" }}
          >
            <NotificationsActiveIcon fontSize="small" />
            &nbsp;Set up Security Center alarms
          </MenuItem>
        )}
        {!isAutopatrol && (
          <MenuItem
            onClick={() => {
              handleClose();
              navigate(`/actionlogs/${siteInfo.id}`);
            }}
            sx={{ fontFamily: "Mulish" }}
          >
            {ActionLogsText}
          </MenuItem>
        )}
        {!isAutopatrol && (
          <MenuItem
            onClick={() => {
              handleClose();
              navigate(getSeeAlertsPath(siteInfo));
            }}
            sx={{ fontFamily: "Mulish" }}
          >
            <ErrorIcon fontSize="small" />
            &nbsp;See Alerts
          </MenuItem>
        )}
        {permissions.delete_cameras && (
          <MenuItem
            onClick={() => {
              handleClose();
              setDeleteSiteDialogOpen(true);
            }}
            sx={{ fontFamily: "Mulish" }}
          >
            <CancelIcon fontSize="small" />
            &nbsp;DELETE Site
          </MenuItem>
        )}
      </Menu>
      {isGenetec && (
        <GenetecAlarmSetupDialog
          open={genetecAlarmDialogOpen}
          siteId={siteInfo.id}
          onClose={() => setGenetecAlarmDialogOpen(false)}
          showStreamCoverage
          navigate={navigate}
        />
      )}
    </div>
  );
};
